import * as React from 'react'
import { useAngularServices } from '@/react/components'
import { generateUUID } from '@/utils'
import { css } from '@emotion/react'
import { SinglePhoto } from './index'
import { cloneDeep } from 'lodash'
import { AddImageIcon } from '@/react/componentAssets'
import EXIF from 'exif-js'

interface Photo {
  id: number | string
  image: string
  deleted?: boolean
  date_created?: string
  latitude?: number
  longitude?: number
}

interface PhotoSectionProps {
  answerLink: Photo[]
  wholeAnswerLink: object //need create full inteface
  debouncedSend: (updates: { field: string; value: unknown }[]) => void
}
interface UploadImageResponse {
  data: {
    id: number | string
    image: string
  }
}

export function PhotoSection({
  answerLink,
  wholeAnswerLink,
  debouncedSend,
}: PhotoSectionProps) {
  const { Api, CurrentUser } = useAngularServices()
  const [photoList, setPhotoList] = React.useState<Photo[]>(
    cloneDeep(answerLink) || [],
  )
  const variableColor = CurrentUser.getClientSettings().web_primary_color

  function convertDMSToDecimal(dms: number[], ref: string): number {
    const [degrees, minutes, seconds] = dms
    let decimal = degrees + minutes / 60 + seconds / 3600
    if (ref === 'S' || ref === 'W') {
      decimal *= -1
    }
    return decimal
  }

  async function addPhoto(event: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files || [])
    const promiseList: Promise<Photo | null>[] = []

    for (const file of files) {
      const blob = new Blob([file], { type: file.type })
      const fileOfBlob = new File([blob], generateUUID())

      const uploadPromise = new Promise<Photo | null>((resolve) => {
        const reader = new FileReader()

        reader.onload = function (e) {
          const imageData = e.target?.result
          if (!imageData || typeof imageData !== 'string') return resolve(null)

          const img = new Image()
          img.src = imageData

          img.onload = function () {
            EXIF.getData(img, function () {
              const allTags = EXIF.getAllTags(this)

              const exifDate =
                allTags.DateTimeOriginal ||
                allTags.CreateDate ||
                allTags.DateTime

              let dateCreated: string
              if (exifDate && typeof exifDate === 'string') {
                dateCreated = convertExifDateToISO(exifDate)
              } else {
                dateCreated = new Date().toISOString()
              }

              const lat =
                allTags.GPSLatitude && allTags.GPSLatitudeRef
                  ? convertDMSToDecimal(
                      allTags.GPSLatitude,
                      allTags.GPSLatitudeRef,
                    )
                  : null

              const lon =
                allTags.GPSLongitude && allTags.GPSLongitudeRef
                  ? convertDMSToDecimal(
                      allTags.GPSLongitude,
                      allTags.GPSLongitudeRef,
                    )
                  : null

              Api.uploadImage(fileOfBlob, {
                date_created: dateCreated,
                lat: lat ?? null,
                lon: lon ?? null,
              }).then((fileResponse: UploadImageResponse) => {
                resolve({
                  id: fileResponse.data.id,
                  image: fileResponse.data.image,
                  date_created: dateCreated,
                  latitude: lat ?? undefined,
                  longitude: lon ?? undefined,
                })
              })
            })
          }

          img.onerror = function () {
            const fallbackDate = new Date().toISOString()
            Api.uploadImage(fileOfBlob, { date_created: fallbackDate }).then(
              (fileResponse: UploadImageResponse) => {
                resolve({
                  id: fileResponse.data.id,
                  image: fileResponse.data.image,
                  date_created: fallbackDate,
                })
              },
            )
          }
        }

        reader.readAsDataURL(file)
      })

      promiseList.push(uploadPromise)
    }

    const responseArray = await Promise.all(promiseList)
    const updatedList = [
      ...photoList,
      ...(responseArray.filter(Boolean) as Photo[]),
    ]
    setPhotoList(updatedList)

    debouncedSend([
      { field: 'photos', value: updatedList },
      { field: 'edited', value: true },
    ])
  }

  const baseStyle = getBaseStyle(variableColor)

  return (
    <section css={baseStyle}>
      <label className="file-input">
        <AddImageIcon height={'auto'} width={'30px'} color={variableColor} />
        <input
          multiple
          type="file"
          onChange={addPhoto}
          accept="image/png, image/gif, image/jpeg"
        />
      </label>
      <div className="photoBlock">
        {photoList.map((photo, idx) =>
          photo.deleted ? null : (
            <SinglePhoto
              key={photo.id + '' + idx}
              fileUrl={photo.image}
              id={photo.id}
              answerLink={answerLink}
              wholeAnswerLink={wholeAnswerLink}
              debouncedSend={debouncedSend}
            />
          ),
        )}
      </div>
    </section>
  )
}

function convertExifDateToISO(exifDate: string): string {
  const match = exifDate.match(
    /^(\d{4}):(\d{2}):(\d{2}) (\d{2}):(\d{2}):(\d{2})$/,
  )
  if (!match) return new Date().toISOString()

  const [, year, month, day, hour, minute, second] = match
  return new Date(
    `${year}-${month}-${day}T${hour}:${minute}:${second}Z`,
  ).toISOString()
}

function getBaseStyle(variableColor: string) {
  return css({
    width: '100%',
    '.file-input': {
      img: {
        width: 30,
      },
      cursor: 'pointer',
      color: variableColor + ' !important',
      input: { display: 'none' },
    },
    '.photoBlock': {
      display: 'flex',
      flexWrap: 'wrap',
    },
  })
}
